// Destination State Step. This step is visible only if the destination country is USA or CAN.

import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  Image
} from "react-native";
import { useTranslation } from 'react-i18next';
import Colors from "../../../../../constants/colors";
import { loadCountries } from "../../../../../services/common.service";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import SearchIcon from "../../../../../../../../assets/images/searchIcon.png";
import RadioSelectedIcon from "../../../../../../../../assets/images/Radio-Select-Button.png";
import FlatListDataComponent from "../../../common/FlatListDataComponent";

type StateItem = { stateid: number; stateAbbrivation: string; stateName: string };

type ChooseStateStepProps = {
  onCountrySelected?: () => void;
};

export default function ChooseStateStep({ onCountrySelected }: ChooseStateStepProps) {
  console.log('ChooseStateStepProps', onCountrySelected)
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [states, setStates] = useState<StateItem[]>([]);
  const selectedDestinationCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const setSelectedDestinationStateAbbr = useSessionStore((s: SessionState) => s.setSelectedDestinationStateAbbr);
  const setSelectedDestinationStateName = useSessionStore((s: SessionState) => s.setSelectedDestinationStateName);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const result = await loadCountries();
        const list = (result?.data ?? []) as any[];
        const country = list.find((c) => c.CountryCode === selectedDestinationCountryCode);
        const countryStates: StateItem[] = Array.isArray(country?.States) ? country.States : [];
        setStates(countryStates);
      } catch (e) {
        console.error('Failed to load states', e);
      }
    };
    fetchStates();
  }, [selectedDestinationCountryCode]);

  // Filter states by search
  const filteredState: StateItem[]  = useMemo(() => {
    const q = search.toLowerCase();
    return states.filter((s) => (s.stateName || '').toLowerCase().includes(q));
  }, [states, search]);

  const onSelectState = (item: StateItem) =>{
    setSelectedState(item.stateName);
    setSelectedDestinationStateName(item.stateName);
    setSelectedDestinationStateAbbr(item.stateAbbrivation);
    // 👇 call back to parent
    onCountrySelected?.();
  }

  return (
    <View style={styles.container}>
      <FlatListDataComponent
        title={t("STATE_STEP_TITLE")}
        subTitle={t("CHOOSE_WHAT_STATE_YOU_ARE_SENDING_TO")}
        searchPlaceHolder={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
        searchValue={search}
        onSearchChangeText={setSearch}
        filteredData={filteredState}
        renderItem={(item) => (
          <TouchableOpacity
            style={styles.countryRow}
            onPress={() => {
              onSelectState(item);
            }}
          >
            <View style={{ flexDirection: 'row'}}>
              <View
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 50,
                  overflow: 'hidden',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: Colors.searchBackground
                }}
              >
                <Text style={[styles.countryName, { paddingLeft: 0, fontSize: 12}]}>{item.stateAbbrivation.slice(0,2)}</Text>
              </View>
              <Text style={styles.countryName}>
                {item.stateName}
              </Text>
            </View>
            {/* <Text style={styles.countryName}>{item.stateName}</Text> */}
            {/* <View
              style={[
                styles.radioOuter,
                selectedState === item.stateName && styles.radioOuterSelected,
              ]}
            >
              {selectedState === item.stateName && <View style={styles.radioInner} />}
            </View> */}
            <View style={styles.radioButton}>
              {selectedState === item.stateName && (
                <Image
                  source={RadioSelectedIcon}
                  style={{ height: 20, width: 20}}   
                  resizeMode='contain'     
                />
              )}
            </View>
          </TouchableOpacity>
        )}
        />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white
  },
  title: {
    fontSize: 28,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 34,
    marginBottom: 8,
    color: Colors.titleTextColor,
  },
  subtitle: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 24,
    marginBottom: 19,
    color: Colors.subTitleColor,
  },
  searchContainer: {
    flexDirection: 'row',
    backgroundColor: Colors.searchBackground,
    padding: 8,
    borderRadius: 24,
    marginBottom: 19,
  },
  searchInput: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 24,
    color: Colors.subTitleColor,
  },
  countryRow: {
    backgroundColor: Colors.white,
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
    borderBottomWidth: 0,
    justifyContent: 'space-between'
  },
  flag: {
    fontSize: 22,
    marginRight: 12,
  },
  countryName: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 20,
    color: Colors.darkGrey,
    paddingLeft: 16
  },
  radioContainer: {
    marginLeft: 12,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.radioButtonFilled
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#D1D5DB",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: '#D1D1D1'
  },
  radioOuterSelected: {
    borderColor: "#000",
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#000",
  },
  selectedText: {
    marginTop: 16,
    fontSize: 16,
    fontWeight: "500",
    color: "#111827",
  },
});
